import type { ReactNode } from 'react';

type EmptyStateProps = {
  /** A Heroicons 24/outline icon, e.g. `<ArchiveBoxIcon className="size-8" />`. */
  icon: ReactNode;
  title: string;
  description?: ReactNode;
  /** Primary call to action (button or anchor). */
  action?: ReactNode;
  /** Quiet teaching line below the action. */
  footnote?: ReactNode;
};

/**
 * Centered teaching / recovery panel for empty data surfaces. Not a card — no
 * border, no shadow. Fills the region it is placed in.
 */
export function EmptyState({ icon, title, description, action, footnote }: EmptyStateProps) {
  return (
    <div className="grid h-full w-full place-items-center p-8">
      <div className="flex max-w-sm flex-col items-center text-center">
        <div className="mb-5 flex size-16 items-center justify-center rounded-full bg-gray-100 text-gray-500">
          {icon}
        </div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {description && <p className="mt-2 text-sm text-gray-600">{description}</p>}
        {action && <div className="mt-6">{action}</div>}
        {footnote && (
          <>
            <div className="mt-5 h-px w-10 bg-gray-200" />
            <p className="mt-4 text-xs leading-relaxed text-gray-400">{footnote}</p>
          </>
        )}
      </div>
    </div>
  );
}
